// Import controller interfaces
import {
    IPolicyController
} from '../Interfaces/ControllerInterfacesBarrel';
// Import api base controller.
import ApiController from './ApiController';
// Import api models.
import {
    ErrorMessage,
    NewBusinessApplication,
    Peril,
    PolicySearchResult,
    PolicyTermPacket,
    PolicyTermWithStatus,
    ProducerInfo,
    PublicPolicyViewData,
} from '@app/models/api';

export default class ProductionPolicyController extends ApiController implements IPolicyController {

    private readonly controllerUrl: string = 'policy';
    private readonly controllerV2Url: string = 'v2/policy';

    public constructor() {
        super();
    }

    public async getAllPolicyTermYears(policyId: number): Promise<number[]> {
        let url: string = `${this.controllerUrl}/termYears/${policyId}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get policy term years.';
            }
            throw error;
        }
    }

    public async getPolicyProducerInfo(policyId: number): Promise<ProducerInfo> {
        let url: string = `${this.controllerV2Url}/${policyId}/producer`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not get policy producer information.';
            }
            throw error;
        }
    }

    public async changePolicyProducerAgent(policyId: number, producerAgentId: number): Promise<{ policyId: number; }> {
        let url: string = `${this.controllerUrl}/producer/user/${policyId}`;

        let data = producerAgentId;

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not change the producer agent.';
            }
            throw error;
        }
    }

    public async changePolicyProducerAgency(policyId: number, producerAgencyId: number): Promise<{ policyId: number; }> {
        let url: string = `${this.controllerUrl}/producer/${policyId}`;

        let data = producerAgencyId;

        try {
            let response: Response = await super.makePostCall(url, data);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not change the producer agency.';
            }
            throw error;
        }
    }

    public async copyPolicyToNewBusinessApplication(policyId: number): Promise<NewBusinessApplication> {
        let url: string = `${this.controllerV2Url}/${policyId}/copy`;

        try {
            let response: Response = await super.makePostCall(url, {});
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not copy the policy.';
            }
            throw error;
        }
    }

    public async canPolicyBeCopiedToNewBusinessApplication(policyId: number): Promise<boolean> {
        let url: string = `${this.controllerV2Url}/${policyId}/validation/copy`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Could not check copy permissions for policy.';
            }
            throw error;
        }
    }

    public async searchPolicies(searchQuery: string): Promise<PolicySearchResult[]> {
        let url: string = `${this.controllerUrl}/search/${searchQuery}`;

        try {
            let response: Response = await super.makeGetCall(url);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = 'Unable to search for policies.';
            }
            throw error;
        }
    }

    public async getPolicyPerils(policyId: number): Promise<Peril[]> {
        let controllerV2Url: string = this.controllerV2Url;
        let url: string = `${controllerV2Url}/perils/${policyId}`;

        try {
            let response: Response = await super.makeGetCall(url, undefined, undefined);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not get peril(s) for policy # ${policyId}.`;
            }
            throw error;
        }
    }

    public async getPolicyPerilsPublic(policyId: number, zipCode: string): Promise<Peril[]> {
        let controllerV2Url: string = this.controllerV2Url;
        let url: string = `${controllerV2Url}/perils/${policyId}/public/${zipCode}`;

        try {
            let response: Response = await super.makeGetCall(url, undefined, undefined, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not get peril(s) for policy # ${policyId}.`;
            }
            throw error;
        }
    }

    public async getPublicPolicyTermsWithStatus(policyId: number, zipCode: string): Promise<PolicyTermWithStatus[]> {
        let controllerV2Url: string = this.controllerV2Url;
        let url: string = `${controllerV2Url}/public/${policyId}/${zipCode}/termsWithStatus`;

        try {
            let response: Response = await super.makeGetCall(url, undefined, undefined, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not get policy terms and their status for policy # ${policyId}.`;
            }
            throw error;
        }
    }

    public async getPublicPolicyViewData(policyId: number, zipCode: string): Promise<PublicPolicyViewData> {
        let controllerV2Url: string = this.controllerV2Url;
        let url: string = `${controllerV2Url}/public/${policyId}/${zipCode}/policyViewData`;

        try {
            let response: Response = await super.makeGetCall(url, undefined, undefined, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not get public policy view data for policy # ${policyId}.`;
            }
            throw error;
        }
    }

    public async getPublicPolicyTermPacket(policyId: number, zipCode: string, termYear: number): Promise<PolicyTermPacket> {
        let controllerV2Url: string = this.controllerV2Url;
        let url: string = `${controllerV2Url}/public/${policyId}/${zipCode}/policyPacketDocument/${termYear}`;

        try {
            let response: Response = await super.makeGetCall(url, undefined, undefined, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not get policy term packet for policy # ${policyId}.`;
            }
            throw error;
        }
    }

    public async getLatestPublicPolicyTermPacket(policyId: number, zipCode: string): Promise<PolicyTermPacket> {
        let controllerV2Url: string = this.controllerV2Url;
        let url: string = `${controllerV2Url}/public/${policyId}/${zipCode}/policyPacketDocument/latest`;

        try {
            let response: Response = await super.makeGetCall(url, undefined, undefined, false);
            return await response.json();
        }
        catch (error) {
            if (ErrorMessage.isErrorMessage(error)) {
                error.message = `Could not get policy term packet for policy # ${policyId}.`;
            }
            throw error;
        }
    }
};